import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Input } from "@/frontend/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/frontend/components/ui/dropdown-menu";
import { Button } from "@/frontend/components/ui/button";
import { ChevronDown, Search, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

export type ChatStatus = "active" | "closed" | "paused";

export interface Chat {
  id: string;
  name: string;
  lastMessage: string;
  lastMessageTime: Date;
  createdAt: Date;
  status: ChatStatus;
}

interface ChatListProps {
  onChatSelect?: (chatId: string) => void;
  selectedChatId?: string;
  initialScrollToChatId?: string;
  accountFilter?: string[];
  viewMode?: "chats" | "incidents";
  onViewModeChange?: (mode: "chats" | "incidents") => void;
}

type SortOption = "lastMessage" | "createdAt";

function stripHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function getRelativeTime(date: Date | string): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} ${diffInWeeks === 1 ? "week" : "weeks"} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths} ${diffInMonths === 1 ? "month" : "months"} ago`;
}

const STORAGE_KEY_CHAT_SORT = "chatList.sortBy";
const STORAGE_KEY_CHAT_STATUS = "chatList.statusFilters";

function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function saveToStorage(key: string, value: any): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silent fail
  }
}

export function ChatList({ onChatSelect, selectedChatId, initialScrollToChatId, accountFilter, viewMode, onViewModeChange }: ChatListProps) {
  const [chats, setChats] = useState<Chat[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>(() =>
    loadFromStorage(STORAGE_KEY_CHAT_SORT, "lastMessage")
  );
  const [statusFilters, setStatusFilters] = useState<Set<ChatStatus>>(() => {
    const saved = loadFromStorage<ChatStatus[]>(STORAGE_KEY_CHAT_STATUS, ["active", "closed", "paused"]);
    return new Set(saved);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [, setTick] = useState(0);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const chatRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const hasScrolledToInitialChat = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    saveToStorage(STORAGE_KEY_CHAT_SORT, sortBy);
  }, [sortBy]);

  useEffect(() => {
    saveToStorage(STORAGE_KEY_CHAT_STATUS, Array.from(statusFilters));
  }, [statusFilters]);

  const fetchChats = useCallback(
    async (offset: number = 0, reset: boolean = false) => {
      if (isLoading) return;

      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          offset: offset.toString(),
          limit: "15",
          sortBy,
        });

        if (searchQuery.trim()) {
          params.append("search", searchQuery);
        }

        if (statusFilters.size > 0) {
          params.append("status", Array.from(statusFilters).join(","));
        }

        if (accountFilter && accountFilter.length > 0) {
          params.append("accounts", accountFilter.join(","));
        }

        const response = await fetch(`/api/chats?${params.toString()}`);
        const data = await response.json();

        setChats((prev) => (reset ? data.chats : [...prev, ...data.chats]));
        setHasMore(data.hasMore);
      } catch (error) {
        console.error("Error fetching chats:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [searchQuery, sortBy, statusFilters, accountFilter, isLoading]
  );

  useEffect(() => {
    setChats([]);
    setHasMore(true);
    fetchChats(0, true);
  }, [searchQuery, sortBy, statusFilters]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoading) {
          fetchChats(chats.length, false);
        }
      },
      { threshold: 1.0 }
    );

    const target = observerTarget.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [chats.length, hasMore, isLoading, fetchChats]);

  useEffect(() => {
    if (initialScrollToChatId && chats.length > 0 && !isLoading && !hasScrolledToInitialChat.current) {
      const chatElement = chatRefs.current.get(initialScrollToChatId);
      if (chatElement && scrollContainerRef.current) {
        chatElement.scrollIntoView({ behavior: "smooth", block: "center" });
        hasScrolledToInitialChat.current = true;
      }
    }
  }, [initialScrollToChatId, chats, isLoading]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchQuery(searchInput);
    }
  };

  const toggleStatusFilter = (status: ChatStatus) => {
    setStatusFilters((prev) => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
  };

  const handleRefresh = () => {
    setChats([]);
    setHasMore(true);
    fetchChats(0, true);
  };

  return (
    <div className="flex h-full max-h-full w-full min-w-0 flex-col border-r bg-background rounded-lg overflow-hidden">
      <div className="flex flex-col gap-3 border-b px-4 py-4">
        <div className="flex justify-center gap-2 pb-2 w-full">
          <Button
            variant={viewMode === "chats" ? "default" : "outline"}
            size="sm"
            onClick={() => onViewModeChange?.("chats")}
          >
            Chats
          </Button>
          <Button
            variant={viewMode === "incidents" ? "default" : "outline"}
            size="sm"
            onClick={() => onViewModeChange?.("incidents")}
          >
            Incidents
          </Button>
        </div>

        <div className="relative -mx-4">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search chats (press Enter)..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="pl-9"
          />
        </div>

        <div className="flex gap-2 -mx-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 bg-transparent"
              >
                Sort: {sortBy === "lastMessage" ? "Last Message" : "Created"}
                <ChevronDown className="ml-auto size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={sortBy}
                onValueChange={(v: string) => setSortBy(v as SortOption)}
              >
                <DropdownMenuRadioItem value="lastMessage">
                  Last Message Time
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="createdAt">
                  Chat Creation Date
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 bg-transparent"
              >
                Status ({statusFilters.size})
                <ChevronDown className="ml-auto size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={statusFilters.has("active")}
                onCheckedChange={() => toggleStatusFilter("active")}
              >
                Active
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilters.has("closed")}
                onCheckedChange={() => toggleStatusFilter("closed")}
              >
                Closed
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilters.has("paused")}
                onCheckedChange={() => toggleStatusFilter("paused")}
              >
                Paused
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={cn("size-4", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto min-h-0 h-0">
        {chats.length === 0 && !isLoading ? (
          <div className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">
            No chats found
          </div>
        ) : (
          <div className="divide-y">
            {chats.map((chat) => (
              <button
                key={chat.id}
                ref={(el) => {
                  if (el) {
                    chatRefs.current.set(chat.id, el);
                  } else {
                    chatRefs.current.delete(chat.id);
                  }
                }}
                onClick={() => onChatSelect?.(chat.id)}
                className={cn(
                  "flex w-full min-w-0 flex-col gap-1 p-4 text-left transition-colors hover:bg-accent",
                  selectedChatId === chat.id && "bg-accent"
                )}
              >
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <h3 className="font-medium text-sm leading-tight truncate min-w-0">
                    {chat.name}
                  </h3>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {getRelativeTime(chat.lastMessageTime)}
                  </span>
                </div>
                <p className="line-clamp-2 text-sm text-muted-foreground leading-relaxed break-words">
                  {stripHtml(chat.lastMessage)}
                </p>
              </button>
            ))}
            {hasMore && (
              <div
                ref={observerTarget}
                className="flex items-center justify-center p-4"
              >
                {isLoading && (
                  <div className="text-sm text-muted-foreground">
                    Loading...
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
